import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useOllie from 'src/hooks/useOllie'
import { noteOllieLimitState } from 'src/hooks/useOllieAtLimit'
import useOllieFindings from 'src/hooks/useOllieFindings'
import useOllieFindingsSaveListener from 'src/hooks/useOllieFindingsSaveListener'
import useOllieVenueId from 'src/hooks/useOllieVenueId'
import OllieChatPanel from './OllieChatPanel'
import OllieFab from './OllieFab'
import OlliePaywallModal from './OlliePaywallModal'
import { useOllieAssistant } from './OllieAssistantContext'
import { getOllieFabBottomOffset } from './ollieFabPlacement'
import { buildOllieFabNotice } from 'src/utils/ollieFabNotice'

const OllieVenueAssistant = () => {
  const location = useLocation()
  const { onVenueDashboard, venueId } = useOllieVenueId()
  const {
    isOpen,
    draftMessage,
    pendingFindingCheckId,
    pendingFindingAction,
    pendingFindingAsk,
    openOllie,
    closeOllie,
    clearDraft,
    clearPendingFinding,
    fabSuppressed,
  } = useOllieAssistant()
  const [statusChecked, setStatusChecked] = useState(false)
  const {
    enabled,
    checkStatus,
    reset,
    limitReached,
    paywall,
    paywallRequested,
    requestPaywall,
    usage,
    limitType,
  } = useOllie()

  const findingsOn = Boolean(
    onVenueDashboard && venueId && statusChecked && enabled,
  )
  const { newFindings } = useOllieFindings({ autoLoad: findingsOn })
  useOllieFindingsSaveListener({ venueId, enabled: findingsOn })
  const fabNotice = buildOllieFabNotice(newFindings)

  const bottomOffset = getOllieFabBottomOffset(location.pathname)
  const showPaywall = Boolean(limitReached && paywall && paywallRequested)

  useEffect(() => {
    if (!onVenueDashboard || !venueId) {
      return undefined
    }

    let isMounted = true

    const loadStatus = async () => {
      setStatusChecked(false)
      await checkStatus()
      if (isMounted) {
        setStatusChecked(true)
      }
    }

    loadStatus()

    return () => {
      isMounted = false
    }
  }, [onVenueDashboard, venueId, checkStatus])

  useEffect(() => {
    closeOllie()
    reset()
  }, [venueId, reset, closeOllie])

  // Ask Ollie links hide at the limit; only once the panel has real usage.
  useEffect(() => {
    if (usage) noteOllieLimitState(venueId, limitReached)
  }, [usage, venueId, limitReached])

  useEffect(() => {
    if (!onVenueDashboard) {
      closeOllie()
    }
  }, [onVenueDashboard, closeOllie])

  if (!onVenueDashboard || !venueId || !statusChecked || !enabled) {
    return null
  }

  return (
    <>
      <OllieFab
        bottomOffset={bottomOffset}
        isOpen={isOpen || fabSuppressed}
        onClick={openOllie}
        notice={fabNotice}
      />
      <OllieChatPanel
        visible={isOpen}
        onClose={closeOllie}
        onShowPaywall={() => requestPaywall(true)}
        draftMessage={draftMessage}
        onDraftConsumed={clearDraft}
        pendingFindingCheckId={pendingFindingCheckId}
        pendingFindingAction={pendingFindingAction}
        pendingFindingAsk={pendingFindingAsk}
        onPendingFindingConsumed={clearPendingFinding}
      />
      <OlliePaywallModal
        visible={showPaywall}
        paywall={paywall}
        usage={usage}
        limitType={limitType}
        venueId={venueId}
        onClose={() => requestPaywall(false)}
      />
    </>
  )
}

export default OllieVenueAssistant
